import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { TooltipContent, TooltipPortal } from "@radix-ui/react-tooltip";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  BarChart2,
  DownloadIcon,
  ExternalLinkIcon,
  ScaleIcon,
} from "lucide-react";
import { useParams } from "react-router-dom";

import TooltipAction from "./TooltipAction";
import { Label } from "./types";

import type { VersionSelectorVersionId } from "@/components/VersionSelector/types";
import { useUser } from "@/context/user-context";
import { EntityType } from "@/queries/folder-entities";
import { useExportPromptVersion } from "@/queries/prompts";

interface VersionTooltipContentProps {
  entityType: EntityType;
  id: VersionSelectorVersionId;
  dynamicReleaseLabelsRegistry: Record<number, Label[]>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allLabels: any[];
  promptId: string | undefined;
  handleABTestButtonClick: (promptId: number, promptVersionId: number) => void;
  handleBacktestButtonClick: (promptVersionId: number) => void;
  handleArchiveButtonClick: (promptVersionId: VersionSelectorVersionId) => void;
  handleUnarchiveButtonClick: (promptVersionId: VersionSelectorVersionId) => void;
  archivingVersionId: VersionSelectorVersionId | null;
  isArchiving: boolean;
  reportId?: number | null;
  isArchived: boolean;
  versionNumber: string | undefined;
  number: number;
  isLatestVersion: boolean;
  latestVersionNumber?: number;
}

const VersionTooltipContent = ({
  entityType,
  id,
  dynamicReleaseLabelsRegistry,
  allLabels,
  promptId,
  handleABTestButtonClick,
  handleBacktestButtonClick,
  handleArchiveButtonClick,
  handleUnarchiveButtonClick,
  archivingVersionId,
  isArchiving,
  reportId,
  isArchived,
  versionNumber,
  number,
  isLatestVersion,
  latestVersionNumber,
}: VersionTooltipContentProps) => {
  const { collectionId, toolId } = useParams();
  const activeWorkspaceId = useUser()?.activeWorkspaceId ?? 0;
  const numericVersionId = typeof id === "number" ? id : null;
  const isThisVersionArchiving = archivingVersionId === id;
  const exportPromptVersion = useExportPromptVersion();
  const isPrompt = entityType === EntityType.PROMPT;
  const isSkillCollection = entityType === EntityType.SKILL_COLLECTION;
  const isTool = entityType === EntityType.TOOL;
  const supportsArchive =
    isPrompt || entityType === EntityType.WORKFLOW || isSkillCollection || isTool;
  return (
    <TooltipPortal container={document.body}>
      <TooltipContent
        className="z-[9999] flex -translate-x-1/2 flex-col gap-1 rounded-md text-xs text-black"
        side="right"
        sideOffset={5}
        align="center"
        style={{ zIndex: 9999 }}
      >
        {isPrompt &&
          numericVersionId !== null &&
          ((!dynamicReleaseLabelsRegistry?.[numericVersionId] && allLabels.length > 0) ||
            dynamicReleaseLabelsRegistry?.[numericVersionId]?.length < allLabels.length) && (
            <TooltipAction
              icon={<ScaleIcon className="inline h-3 w-3" />}
              label="Start A/B Test"
              onClick={() => handleABTestButtonClick(Number(promptId), numericVersionId)}
            />
          )}
        {reportId && (
          <TooltipAction
            icon={<ExternalLinkIcon className="inline h-3 w-3" />}
            label="Open Evaluation"
            to={`/workspace/${activeWorkspaceId}/evaluate/reports/${reportId}`}
            openInNewTab
          />
        )}
        {isPrompt && Number(versionNumber) !== number && (
          <TooltipAction
            icon={<MagnifyingGlassIcon className="inline h-3 w-3" />}
            label="View Diff"
            to={`/workspace/${activeWorkspaceId}/prompt/${promptId}/version/${versionNumber}/diff/${number}`}
          />
        )}
        {isSkillCollection && Number(versionNumber) !== number && (
          <TooltipAction
            icon={<MagnifyingGlassIcon className="inline h-3 w-3" />}
            label="View Diff"
            to={`/workspace/${activeWorkspaceId}/skill-collection/${collectionId}/version/${versionNumber}/diff/${number}`}
          />
        )}
        {isTool && !isLatestVersion && latestVersionNumber && (
          <TooltipAction
            icon={<MagnifyingGlassIcon className="inline h-3 w-3" />}
            label="View Diff"
            to={`/workspace/${activeWorkspaceId}/tool/${toolId}/version/${latestVersionNumber}/diff/${number}`}
          />
        )}
        {isPrompt && !isLatestVersion && (
          <TooltipAction
            icon={<BarChart2 className="inline h-3 w-3" />}
            label="Run Backtest"
            onClick={() => handleBacktestButtonClick(Number(id))}
          />
        )}
        {isPrompt && promptId && (
          <TooltipAction
            icon={<DownloadIcon className="inline h-3 w-3" />}
            label="Export"
            onClick={() =>
              exportPromptVersion.mutate({
                workspaceId: activeWorkspaceId,
                promptRegistryId: Number(promptId),
                versionNumber: number,
              })
            }
            isLoading={exportPromptVersion.isLoading}
          />
        )}
        {supportsArchive && !isArchived && (
          <TooltipAction
            icon={<ArchiveIcon className="inline h-3 w-3" />}
            label="Hide Version"
            onClick={() => handleArchiveButtonClick(id)}
            disabled={isArchiving}
            isLoading={isThisVersionArchiving}
          />
        )}
        {supportsArchive && isArchived && (
          <TooltipAction
            icon={<ArchiveRestoreIcon className="inline h-3 w-3" />}
            label="Restore Version"
            onClick={() => handleUnarchiveButtonClick(id)}
            disabled={isArchiving}
            isLoading={isThisVersionArchiving}
          />
        )}
      </TooltipContent>
    </TooltipPortal>
  );
};

export default VersionTooltipContent;
